import React from 'react';
import DrawingViewer from '../../DrawingViewer';
import ModelViewer from '../../ModelViewer';
import { SHOW_MODEL_TOOLBAR } from '../../../config/experiment';
import { matchGazePositions, matchGazeDirections, revealedViewTiltAngles } from '../../buttons/MatchGazeButton';
import {
  showHintPositions,
  showHintTargets,
  showHintDescriptions,
  defaultCameraPosition,
} from '../cameraPositions';

// GazeControl（視点合わせ演習）の3カラムを構成する表示部品。
// 状態は持たず、GazeControl（useGazeMatchState）から受け取って描画に徹する。

// 3Dキャンバス上の小見出し。視点移動アニメーション中だけ「どちらへずれていくか」に置き換える
// （2026-10-19：キャンバスに吹き出しを重ねるとモデルが隠れて邪魔だったため、見出しの行で示す）
function ViewCaption({ show, errorGlbPath, children }) {
  const text = matchGazeDirections[errorGlbPath];
  if (show && text) return <p className="text-subtitle mb-1 gaze-direction-note">{text}</p>;
  return <p className="text-subtitle mb-1">{children}</p>;
}

// 左カラム：モチーフ（デッサン＋既定視点の3Dモデル）
export function MotifColumn({ motifGlbPath, selectedPart }) {
  return (
    <div className="gaze-column w-1/3 flex flex-col items-center">
      <h2 className="heading-lg mb-2">モチーフ</h2>
      <h3 className="text-subtitle mb-1">デッサン</h3>
      <DrawingViewer glbPath={motifGlbPath} caption="左" canvasId="leftDrawingCanvas" />
      <h3 className="text-subtitle mt-3 mb-1">3Dモデル</h3>
      <p className="text-subtitle mb-1">もとの視点（斜め上から）</p>
      <ModelViewer
        glbPath={motifGlbPath}
        imagePath={`./drawing/${motifGlbPath}.png`}
        caption="左"
        canvasId="leftModelCanvas"
        addColor={selectedPart}
        showToolbar={SHOW_MODEL_TOOLBAR}
      />
    </div>
  );
}

// 中央カラム：誤り（視点合わせアニメーションの対象。差替後はモチーフモデルを表示）
export function ErrorColumn({
  errorGlbPath,
  errorViewGlbPath,
  selectedPart,
  errorCameraPos,
  isGazeAnimating,
  viewerRef,
}) {
  return (
    <div className="gaze-column w-1/3 flex flex-col items-center">
      <h2 className="heading-lg mb-2">誤り</h2>
      <h3 className="text-subtitle mb-1">デッサン</h3>
      <DrawingViewer glbPath={errorGlbPath} caption="右" canvasId="rightDrawingCanvas" />
      <h3 className="text-subtitle mt-3 mb-1">3Dモデル</h3>
      <ViewCaption show={isGazeAnimating} errorGlbPath={errorGlbPath}>もとの視点（斜め上から）</ViewCaption>
      <div className="w-full">
        <ModelViewer
          ref={viewerRef}
          glbPath={errorViewGlbPath}
          imagePath={`./drawing/${errorViewGlbPath}.png`}
          caption="右"
          canvasId="rightModelCanvas"
          addColor={selectedPart}
          externalNewCameraPosition={errorCameraPos}
          showToolbar={SHOW_MODEL_TOOLBAR}
        />
      </div>
    </div>
  );
}

// 右カラム：おかしさが分かる視点（研究用語では「顕在化された視点」。固定ヒントカメラ＋目オブジェクト）。
// UIの見出し・説明は学習者（初学者）の言葉で書く：研究側の用語（顕在化・既定など）を出さない。
// 左・中カラムの「デッサン」ブロックと同じ高さ（h-48）の説明ボックスを置き、
// 3カラムの3Dモデルのキャンバスが横一列に揃うようにしている。
export function RevealedGazeColumn({
  errorGlbPath,
  selectedPart,
  showMotifModel,
  isGazeAnimating,
  viewerRef,
}) {
  return (
    <div className="gaze-column w-1/3 flex flex-col items-center">
      {/* このカラムのモデルは差し替えず、常に誤りモデルを表示する（2026-10-19）。
          成り立つ関係は「誤り@一致視点 ≈ モチーフ@既定視点」なので、一致視点の目と組になるのは
          誤りモデル。モチーフへ差し替えて目を残すと「モチーフ＋一致視点の目」という
          成り立たない位置関係を示してしまう（例 model1：低い位置から見たモチーフが正しく見える、と読める）。
          persistentEyePosition: 目オブジェクト（オレンジ）を常時静的配置する。
          初期表示〜アニメ前は既定視点位置。視点を合わせる間と合わせた後は「画面が目と一緒に動く」
          見え方にする（2026-10-19）：目は既定視点の位置に止まって見え、3Dモデルのほうが逆向きに回る
          （アニメ中は MatchGazeButton の playEyeAnimation、合わせた後は persistentEyeFrom で同じ見え方を置く）。
          学習者の視点が動くと3Dモデルが回って見える「誤り」列と、動き方の見え方をそろえるため */}
      <h2 className="heading-lg mb-2">おかしさが分かる視点</h2>
      <h3 className="text-subtitle mb-1">この画面の見方</h3>
      <div className="w-full h-48 gaze-legend">
        <p>
          この画面は、「誤り」の3Dモデルと<span className="gaze-legend__eye">オレンジの目印</span>を外から眺めたものです。
          目印は「どこから見ているか」、矢印は視点が動く向きを表します。視点が動くと、この画面も目印と一緒に動きます。
        </p>
      </div>
      <h3 className="text-subtitle mt-3 mb-1">3Dモデル</h3>
      <ViewCaption show={isGazeAnimating} errorGlbPath={errorGlbPath}>
        {showHintDescriptions[errorGlbPath] || '別の角度から見たようす'}
      </ViewCaption>
      <div className="w-full">
        <ModelViewer
          ref={viewerRef}
          glbPath={errorGlbPath}
          imagePath={`./drawing/${errorGlbPath}.png`}
          caption="誤"
          canvasId="wrongViewModelCanvas"
          addColor={selectedPart}
          cameraPosition={showHintPositions[errorGlbPath]}
          cameraTarget={showHintTargets[errorGlbPath]}
          cameraTiltAngle={revealedViewTiltAngles[errorGlbPath] || 0}
          persistentEyePosition={showMotifModel ? matchGazePositions[errorGlbPath] : defaultCameraPosition}
          persistentEyeFrom={showMotifModel ? defaultCameraPosition : null}
          // 横からの画面（model1・cube_model5）では目印が左上に寄るので、軸の凡例は右上に置く（目印を隠さない）
          axisLegendPlacement="top-right"
          showToolbar={SHOW_MODEL_TOOLBAR}
        />
      </div>
    </div>
  );
}
